import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { WithKeys } from 'lib_ts/interfaces/common/i-generics';
import { IHitter, IMergeHitterSuggestion } from 'lib_ts/interfaces/i-hitter';

/** Minimum number of shared name tokens required to consider two hitters a potential match. */
const MIN_KEYS_INTERSECTION_TO_SUGGEST = 2;

/** Maximum number of merge suggestions returned per source hitter. */
const MAX_SUGGESTIONS = 3;

/** Helpers for fuzzy-matching and merging hitter records from different data sources. */
export class HittersHelper {
  /**
   * For each source hitter (manual / non-API), finds the best-matching suggestion
   * hitters (from the stats API) by comparing tokenized name similarity.
   *
   * Matching rules:
   * - Hitter batting side must agree (important for switch hitters with separate entries).
   * - Names are tokenized via `keyify` and scored by the number of shared tokens.
   * - Only suggestions meeting the minimum token overlap are kept.
   * - Results are ranked by score (descending) and capped at MAX_SUGGESTIONS per source.
   */
  static getMergeCandidates(
    sources: IHitter[], // will receive data from suggestions
    suggestions: IHitter[] // will be removed after merging into sources
  ): IMergeHitterSuggestion[] {
    if (sources.length === 0) {
      // nothing to work on
      return [];
    }

    if (suggestions.length === 0) {
      // nothing to suggest from
      return [];
    }

    // pre-tokenize all suggestion names once so we don't repeat work per source
    const keyedSuggestions = suggestions.map((m) => {
      const o: WithKeys<{ hitter: IHitter }> = {
        hitter: m,
        keys: MiscHelper.keyify(m.name),
      };

      return o;
    });

    const output: IMergeHitterSuggestion[] = [];

    sources.forEach((source) => {
      // tokenize the source hitter's name for comparison
      const sourceKeys = MiscHelper.keyify(source.name);

      const matches: {
        hitter: IHitter;
        score: number;
      }[] = [];

      keyedSuggestions.forEach((suggestion) => {
        if (source.side !== suggestion.hitter.side) {
          // only match where the side agrees, mainly for switch hitters
          return;
        }

        // score = number of name tokens the two hitters share
        const nameScore = ArrayHelper.countIntersection(
          suggestion.keys,
          sourceKeys
        );

        if (nameScore < MIN_KEYS_INTERSECTION_TO_SUGGEST) {
          return;
        }

        matches.push({
          hitter: suggestion.hitter,
          score: nameScore,
        });
      });

      if (matches.length === 0) {
        return;
      }

      // highest scores first
      matches.sort((a, b) => (a.score > b.score ? -1 : 1));

      // keep only the top N suggestions for this source hitter
      output.push({
        source: source,
        suggestions: matches.slice(0, MAX_SUGGESTIONS).map((m) => m.hitter),
      });
    });

    return output;
  }
}
